import { lowerCase } from 'lodash-es';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { TASK_CONST } from '@/constant';
import type { TaskFrameStoreActions, TaskFrameStoreState } from '@/type';

import useAuthStore from './authStore';

const INIT_STATE: TaskFrameStoreState = {
  allFrameList: [],
  cameraCalibrationConfigMap: {},
  currentFrame: null,
  enabledSubmit: false,
  frameCount: {
    completed: 0,
    rejected: 0,
    submitted: 0,
    total: 0,
  },
  isFrameDataLoading: false,
  isFrameFileLoading: false,
  isNotFoundedFrame: false,
  isUpsertLabelingPending: false,
  latestTrackSession: {
    frameSeq: null,
    id: null,
  },
  nextFrame: null,
  nextReview: null,
  metaData: null,
  prevFrame: null,
  prevReview: null,
  reviewComment: null,
  reviewData: null,
  selectedTask: null,
  selectedTaskId: null,
};

const useTaskFrameStore = createWithEqualityFn<
  TaskFrameStoreState & TaskFrameStoreActions
>((set, get) => {
  const assert = () => {
    if (get().currentFrame == null) return false;

    return true;
  };
  const getUser = () => useAuthStore.getState().user;
  const isWorkStateFactory =
    (workState: TASK_CONST.TASK_FRAME_WORKSTATE) => () =>
      get().isWorkState(workState);

  const isDone = isWorkStateFactory(TASK_CONST.TASK_FRAME_WORKSTATE.DONE);
  const isPrework = isWorkStateFactory(TASK_CONST.TASK_FRAME_WORKSTATE.PREWORK);
  const isReject = isWorkStateFactory(TASK_CONST.TASK_FRAME_WORKSTATE.REJECT);
  const isReview = isWorkStateFactory(TASK_CONST.TASK_FRAME_WORKSTATE.REVIEW);
  const isReviewing = isWorkStateFactory(
    TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING,
  );
  const isViewOnly = isWorkStateFactory(
    TASK_CONST.TASK_FRAME_WORKSTATE.VIEW_ONLY,
  );
  const isWorking = isWorkStateFactory(TASK_CONST.TASK_FRAME_WORKSTATE.WORKING);

  return {
    ...INIT_STATE,

    clear() {
      set({ ...INIT_STATE });
    },

    getAssignedMeFrameList() {
      const frameList = get().allFrameList;
      const user = getUser();

      return frameList.filter(f => f?.workerID === user.id);
    },

    getPcdFileNameList() {
      return get().allFrameList.map(f => f?.filename);
    },

    getPrevWorkState() {
      const currentFrame = get().currentFrame!;

      if (isWorking())
        return currentFrame?.rejectCount > 0
          ? TASK_CONST.TASK_FRAME_WORKSTATE.REJECT
          : TASK_CONST.TASK_FRAME_WORKSTATE.PREWORK;
      if (isReview()) return TASK_CONST.TASK_FRAME_WORKSTATE.WORKING;
      if (isReject() || isDone())
        return TASK_CONST.TASK_FRAME_WORKSTATE.REVIEWING;

      return get().currentFrame?.workState;
    },

    hasFrame(index) {
      const frameData = get().allFrameList[index];

      return frameData != null && frameData.frameSeq === index + 1;
    },

    hasRevertPermissionByRole() {
      return (
        get().isRevertPermissionForDoneOrReject() ||
        get().isRevertPermissionForReview() ||
        get().isRevertPermissionForWorking()
      );
    },

    initFrameCount() {
      set({
        frameCount: INIT_STATE.frameCount,
      });
    },

    isAssigned(user) {
      return (
        get().isValidFrameDetail() && get().currentFrame!.workerID === user.id
      );
    },

    isAssignedMe() {
      const user = getUser();

      return get().isAssigned(user);
    },

    isAssignedReview(user) {
      const reviewerID = get().currentFrame?.reviewerID;

      return (
        (isReviewing() || isReview()) &&
        (useAuthStore.getState().isManager() ||
          useAuthStore.getState().isAdmin()) &&
        get().isValidFrameDetail() &&
        (reviewerID === '' || reviewerID === user.id)
      );
    },

    isAssignedReviewMe() {
      const user = getUser();

      return get().isAssignedReview(user);
    },

    isRevertDisabledByWorkState() {
      return isPrework() || isReviewing() || isViewOnly();
    },

    isRevertPermissionForDoneOrReject() {
      const isManager = useAuthStore.getState().isManager();

      return (isDone() || isReject()) && isManager;
    },

    isRevertPermissionForReview() {
      const isManager = useAuthStore.getState().isManager();
      const isWorker = useAuthStore.getState().isWorker();
      const isAssignedMe = get().isAssignedMe();

      return isReview() && (isManager || (isWorker && isAssignedMe));
    },

    isRevertPermissionForWorking() {
      const isAssignedMe = get().isAssignedMe();

      return isWorking() && isAssignedMe;
    },

    isWorkingRevertConditionSatisfied() {
      const currentFrame = get().currentFrame;
      const rejectCount = currentFrame?.rejectCount ?? 0;
      const currentWorkData = currentFrame?.workData;

      return isWorking() && rejectCount === 0
        ? currentWorkData == null || currentWorkData.length === 0
        : true;
    },

    isWorkState(workState) {
      if (!assert()) return false;

      return lowerCase(get().currentFrame!.workState) === lowerCase(workState);
    },

    isValidFrameDetail() {
      if (!assert()) return false;

      return get().currentFrame!.frameSeq > 0;
    },

    selectTask(task) {
      if (!task) return;

      let pcdPath = task.basePath.replace(/^\//gi, '');

      pcdPath += `${task.basePath ? '/' : ''}${task.orgPath}`;

      set({
        selectedTask: {
          ...task,
          pcdPath,
        },
        selectedTaskId: task?.id,
      });
    },

    setAllFrameList(allFrameList) {
      Array.isArray(allFrameList)
        ? set({ allFrameList })
        : set(state => ({
            ...state,
            allFrameList: allFrameList(state.allFrameList),
          }));
    },

    setCameraCalibrationConfigMap(cameraCalibrationConfigMap) {
      set({ cameraCalibrationConfigMap });
    },

    setCurrentFrame(frameData) {
      const trackSessionID =
        frameData.curTrackSession?.id ?? frameData.usableTrackSession?.id;

      set({
        currentFrame: frameData,
        latestTrackSession: {
          frameSeq: trackSessionID
            ? frameData.frameSeq
            : get().latestTrackSession?.frameSeq,
          id: trackSessionID ?? get().latestTrackSession?.id,
        },
      });
    },

    setEnabledSubmit(enabledSubmit) {
      set({
        enabledSubmit,
      });
    },

    setFrame(frame, index) {
      const allFrameList = [...get().allFrameList];

      allFrameList[index] = frame;

      get().setAllFrameList(allFrameList);

      return frame;
    },

    setFrameCount(...args: any[]) {
      if (args.length === 1) {
        set({ frameCount: args[0] });
        return;
      }

      set({
        frameCount: {
          ...get().frameCount,
          [args[0]]: args[1],
        },
      });
    },

    setIsFrameDataLoading(isFrameDataLoading) {
      set({ isFrameDataLoading });
    },

    setIsFrameFileLoading(isFrameFileLoading) {
      set({ isFrameFileLoading });
    },

    setIsNotFoundedFrame(isNotFoundedFrame) {
      set({ isNotFoundedFrame });
    },

    setIsUpsertLabelingPending(isUpsertLabelingPending) {
      set({ isUpsertLabelingPending });
    },

    setMetaData(metaData) {
      set({ metaData });
    },
  };
}, shallow);

export default useTaskFrameStore;
